/**
 * Container for Toasts, MiniCoachmark, CoachmarkDialog, SystemDialog
 *
 * @module containers/OverlayContainer
 *
 * This container listens for state change in state.overlay, and renders the
 * appropriate overlay based on the type and id of the highest priority overlay.
 *
 * This is a refactor in progress. The goal is for this component to only
 * render 4 possible components: Toast, MiniCoachmark, CoachmarkDialog, SystemDialog
 */
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';
import { bindActionCreators } from 'redux';

import * as Actions from '../../actions';
import NavHamburger from '../../components/lib/NavHamburger/NavHamburger';
import ModalCoachMark from '../../components/Modal/ModalCoachMark';
import ModalCoachmarkDialog from '../../components/Modal/ModalCoachmarkDialog';
import ModalCustom from '../../components/Modal/ModalCustom';
import ModalPremiumWelcome from '../../components/Modal/ModalPremiumWelcome';
import ModalShare from '../../components/Modal/ModalShare';
// TODO: Kept for backwards compatibility
import ModalStandard from '../../components/Modal/ModalStandard';
import ModalUpsell from '../../components/Modal/ModalUpsell';
import * as Constants from '../../constants';
import * as Utils from '../../utils';

import './OverlayContainer.scss';

class OverlayContainer extends React.Component {
  static propTypes = {
    closeOverlay: PropTypes.func,
    disableBodyScrolling: PropTypes.func,
    enableBodyScrolling: PropTypes.func,
    overlay: PropTypes.object,
    overlayProps: PropTypes.object,
  };

  state = {
    previousScrollTop: 0,
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.overlay && this.props.overlay) {
      if (this.getShouldDisableBodyScroll(this.props.overlay)) {
        this.props.disableBodyScrolling(this.isFixedPositioning());
        const scrollTop = Utils.Scroll.getScrollAmount();
        this.setPreviousScrollTop(scrollTop);
      }
      return;
    }
    if (prevProps.overlay && !this.props.overlay) {
      if (this.getShouldDisableBodyScroll(prevProps.overlay)) {
        this.props.enableBodyScrolling();
        Utils.Scroll.scrollToTop(this.state.previousScrollTop);
      }
      this.setPreviousScrollTop(0);
      return;
    }
    if (prevProps.overlay && this.props.overlay && prevProps.overlay.ID !== this.props.overlay.ID) {
      if (!this.getShouldDisableBodyScroll(this.props.overlay)) {
        this.props.enableBodyScrolling();
        if (this.state.previousScrollTop > 0) {
          Utils.Scroll.scrollToTop(this.state.previousScrollTop);
        }
        this.setPreviousScrollTop(0);
      } else if (this.getShouldDisableBodyScroll(this.props.overlay)) {
        this.props.disableBodyScrolling(this.isFixedPositioning());
        const scrollTop = Utils.Scroll.getScrollAmount();
        this.setPreviousScrollTop(scrollTop);
      }
    }
  }

  componentWillUnmount() {
    this.props.enableBodyScrolling();
  }

  isFixedPositioning = () => {
    const overlayToRender = this.props.overlay;
    if (overlayToRender && this.props.overlayProps) {
      return (
        overlayToRender[Constants.Overlay.OVERLAY_CONFIG.TYPE] ===
        Constants.Overlay.OVERLAY_TYPES.hamburgerMenu
      );
    }
    return false;
  };

  // temporary un-optimized solution to be replaced by WEBCLIENT-5141 after
  // react 16.3.2 upgrade task: WEBCLIENT-5139 to move away from setting state in componentDidMount
  setPreviousScrollTop(previousScrollTop) {
    this.setState({ previousScrollTop });
  }

  doCloseOverlay = overlayToRender => () => {
    this.props.closeOverlay(overlayToRender[Constants.Overlay.OVERLAY_CONFIG.ID]);
  };

  renderOverlay = () => {
    const overlayToRender = this.props.overlay;
    if (overlayToRender && this.props.overlayProps) {
      switch (overlayToRender[Constants.Overlay.OVERLAY_CONFIG.TYPE]) {
        case Constants.Overlay.OVERLAY_TYPES.hamburgerMenu:
          return <NavHamburger {...this.props.overlayProps} />;
      }
    }
    return null;
  };

  // TODO: Move the rendering of all these "modals" into renderOverlay
  renderModal = () => {
    const modalToRender = this.props.overlay;
    const modalProps = {
      // copy over modal properties
      ...modalToRender[Constants.Overlay.OVERLAY_CONFIG.PROPS],
    };
    const closeCallback = modalProps.onClose;
    modalProps.onClose = closeCallback
      ? () => {
          closeCallback();
          this.doCloseOverlay(modalToRender)();
        }
      : this.doCloseOverlay(modalToRender);
    switch (modalToRender[Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]) {
      case 'ModalStandard':
        return (
          <ModalStandard {...modalProps} key={modalToRender[Constants.Overlay.OVERLAY_CONFIG.ID]} />
        );
      case 'ModalCoachMark':
        return (
          <ModalCoachMark
            {...modalProps}
            key={modalToRender[Constants.Overlay.OVERLAY_CONFIG.ID]}
          />
        );
      case Constants.Overlay.PremiumWelcomeModalSubtype:
        return (
          <ModalPremiumWelcome
            {...modalProps}
            key={modalToRender[Constants.Overlay.OVERLAY_CONFIG.ID]}
          />
        );
      case Constants.Overlay.SimStreamModalSubtype:
      case 'ModalCustom':
        return (
          <ModalCustom {...modalProps} key={modalToRender[Constants.Overlay.OVERLAY_CONFIG.ID]} />
        );
      case 'ModalUpsell':
        return (
          <ModalUpsell {...modalProps} key={modalToRender[Constants.Overlay.OVERLAY_CONFIG.ID]} />
        );
      case 'ModalShare':
        return (
          <ModalShare {...modalProps} key={modalToRender[Constants.Overlay.OVERLAY_CONFIG.ID]} />
        );
      case 'ModalCoachmarkDialog':
        return (
          <ModalCoachmarkDialog
            {...modalProps}
            key={modalToRender[Constants.Overlay.OVERLAY_CONFIG.ID]}
          />
        );
      default:
        return null;
    }
  };

  getShouldDisableBodyScroll = overlay => {
    if (overlay) {
      // The overlayType/overlaySubtype check here is only necessary while the other overlays
      // get fully refactored: WEBCLIENT-5061
      const overlayType = overlay[Constants.Overlay.OVERLAY_CONFIG.TYPE];
      const overlaySubtype = overlay[Constants.Overlay.OVERLAY_CONFIG.SUBTYPE];
      const overlayTypeToCheck = overlaySubtype || overlayType;
      switch (overlayTypeToCheck) {
        case Constants.Overlay.OVERLAY_TYPES.hamburgerMenu:
        case 'ModalShare':
        case 'ModalStandard':
        case Constants.Overlay.SimStreamModalSubtype:
        case 'ModalCoachmarkDialog':
        case 'ModalCustom':
        case 'ModalUpsell':
        case Constants.Overlay.PremiumWelcomeModalSubtype:
          return true;
        default:
          return false;
      }
    }
    return false;
  };

  render() {
    const overlay = this.props.overlay;
    let overlayToRender = null;
    if (overlay) {
      switch (overlay[Constants.Overlay.OVERLAY_CONFIG.TYPE]) {
        case Constants.Overlay.OVERLAY_TYPES.hamburgerMenu:
          overlayToRender = this.renderOverlay();
          break;
        default:
          overlayToRender = this.renderModal();
      }
    }
    return (
      <ReactCSSTransitionGroup
        transitionName='fx-overlay'
        transitionEnterTimeout={300}
        transitionLeaveTimeout={200}
      >
        {overlayToRender}
      </ReactCSSTransitionGroup>
    );
  }
}

export function mapStateToProps(state) {
  const overlay = Utils.Overlay.shouldShowOverlay(state)
    ? Utils.Overlay.getHighestPriorityOverlay(state)
    : null;
  let overlayProps = null;
  if (overlay) {
    overlayProps = Utils.Overlay.getOverlayProps(state, overlay);
  }
  return {
    overlay,
    overlayProps,
  };
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      disableBodyScrolling: Actions.Scroll.disableBodyScrolling,
      enableBodyScrolling: Actions.Scroll.enableBodyScrolling,
      closeOverlay: Actions.Overlay.closeOverlay,
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(OverlayContainer);
